import { Close, Delete, Download, FilterAlt } from "@mui/icons-material";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Select,
  Tooltip,
} from "@mui/material";
import DrawerCM from "components/shared/Drawer";
import generateHeadCells from "helper/generate-head-cell";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  StyledGridIcons,
  StyledMenuItem,
  StyledStack,
} from "./DisplayClassFiles.style";

import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import useHttp from "hooks/use-http";
import { PAGINATION_LIMIT } from "constants";
import {
  getEducationYearRequest,
  getStudentClassFileRequest,
} from "api/requests";
import { useParams } from "react-router-dom";
import { useEffect } from "react";
import { FILE_BASE_URL } from "constants";
import useDownloader from "react-use-downloader";
import { toast } from "react-toastify";
import LoadingCM from "components/shared/Loading";
import BackButton from "components/shared/BackButton";

const headCells = generateHeadCells("class_file");

export default function ClassFiles() {
  const { class_id } = useParams();

  const { download, error, cancel } = useDownloader();

  const { register, control } = useForm();

  // filter option state
  const [selectedEducationalYear, setSelectedEducationalYear] = useState("");
  const [classFiles, setClassFiles] = useState({
    data: [{ content: "اطلاعاتی یافت نشد" }],
    count: 0,
  });

  // class name state
  const [className, setClassName] = useState("c22");

  // http hook
  const { loading, request, setLoading } = useHttp(false);

  // paginition state
  const [page, setPage] = useState(1);

  //handle download
  const handleDownload = (id, fileUrl) => {
    const fileName = fileUrl.split("/");
    download(FILE_BASE_URL + fileUrl, fileName[fileName.length - 1]);
  };

  const getClassFiles = async (filter = true) => {
    setLoading(true);
    const query = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      educational_year: selectedEducationalYear?.value ?? null,
      class_id,
    };

    const res = await request({
      url: getStudentClassFileRequest,
      values: filter
        ? query
        : {
            skip: 0,
            limit: PAGINATION_LIMIT,
            educational_year: "*",
            class_id,
          },
    });

    if (!res.count || !res.data.length) {
      return setClassFiles({
        data: [{ content: "اطلاعاتی یافت نشد" }],
        count: 0,
      });
    }

    const transformedData = res.data.map((item) => ({
      id: 3,
      title: item.title ?? "ثبت نشده",
      sender_type: item.added_by_type === "teacher" ? "مدرس" : "ادمین",
      educational_year: item.educational_year ?? "ثبت نشده",
      date: item.created_at.string ?? "ثبت نشده",
      file: (
        <IconButton
          color="primary"
          onClick={() => handleDownload(item.id, item.file)}
        >
          <Tooltip title="دانلود فایل">
            <Download />
          </Tooltip>
        </IconButton>
      ),
    }));

    setClassFiles({
      data: transformedData,
      count: res.count,
    });
    setClassName(res.data[0].class.title);

    setLoading(false);
  };

  const getWithFilter = () => {
    setPage(1);
    getClassFiles();
  };

  const getWithoutFilter = () => {
    setPage(1);
    setSelectedEducationalYear(null);
    setSelectedEducationalYear("");
    getClassFiles(false);
  };

  const [educationalYears, setEducationalYears] = useState([]);
  const getEducationYear = useCallback(async () => {
    setLoading(true);
    const res = await request({
      url: getEducationYearRequest,
      isMulty: true,
    });
    if (res) {
      setEducationalYears(res);
      setSelectedEducationalYear({ value: res[res.length - 2] });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    getEducationYear();
    getClassFiles();
  }, [page, getEducationYear]);

  useEffect(() => {
    if (error) {
      cancel();
      toast.error("مشکلی در دانلود پیش آمد", { position: "top-center" });
    }
  }, [error]);

  return (
    <DrawerCM title={" فایل های کلاس " + className} $styledHeader>
      <Grid item mt={3} xs={12}>
        <BackButton />
      </Grid>
      {loading && <LoadingCM />}
      <StyledStack>
        <Grid container>
          <Grid item sm={6} xs={12} p={0.5}>
            <Controller
              control={control}
              defaultValue={""}
              name="select-educational-year"
              render={() => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-educational-year">
                    انتخاب سال تحصیلی
                  </InputLabel>
                  <Select
                    id="educationalYear"
                    name="educationalYear"
                    label="انتخاب سال تحصیلی"
                    labelId="select-educational-year"
                    value={selectedEducationalYear?.value ?? ""}
                    {...register("educationalYear")}
                    onChange={(e) =>
                      setSelectedEducationalYear({
                        value: e.target.value,
                      })
                    }
                  >
                    <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                    {educationalYears.map((item, index) => {
                      return (
                        <StyledMenuItem key={index} value={item}>
                          {item + 1} - {item}
                        </StyledMenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={4} p={0.5}></Grid>
          <StyledGridIcons item sm={2} xs={12} p={1}>
            <IconButton color="primary" onClick={getWithFilter}>
              <Tooltip title="اعمال فیلتر" placement="top">
                <FilterAlt />
              </Tooltip>
            </IconButton>
            <IconButton color="error" onClick={getWithoutFilter}>
              <Tooltip title="حذف فیلتر" placement="top">
                <Delete />
              </Tooltip>
            </IconButton>
          </StyledGridIcons>
        </Grid>
      </StyledStack>
      <Grid container>
        <Grid item xs={12} my={2}>
          <TableCM
            rows={classFiles.data}
            page={page}
            headCells={headCells}
            colspan={classFiles.count === 0 ? "10" : ""}
            fontSize="12px"
            cellFontSize="12px"
          />
          {classFiles.data.length < 1 ? (
            ""
          ) : (
            <PaginationCM
              handleChange={(_, val) => setPage(val)}
              page={page}
              count={classFiles.count}
            />
          )}
        </Grid>
      </Grid>
    </DrawerCM>
  );
}
